import {
  IIntelliUploadRepository,
  IIntelliUploadNewDocumentRequest,
  IIntelliUploadNewDocumentResponse,
  IIntelliUploadClearIntelliUploadDocumentsResponse,
  IIntelliUploadClearIntelliUploadDocumentsRequest,
  IIntelliUploadProcessDocumentsRequest,
  IIntelliUploadProcessDocumentsResponse,
  IIntelliUploadInitiateRuleEngineRequest,
  IIntelliUploadInitiateRuleEngineResponse,
  IIntelliUploadHistoryResponse,
  IIntelliUploadHistoryRequest,
} from "@/core/repositories/intelli-upload";
import { PrivateHttp } from "@/infrastructure/adapters/http";
import { ApiEndpoints, AppConfig } from "@/infrastructure/services/config";
import { ApolloClient } from "@apollo/client";
import { ContentType } from "@ir/core/http";
import {
  CLEAR_INTELLI_UPLOAD_DOCUMENTS,
  GET_INTELLI_UPLOAD_HISTORY,
  GET_INTELLI_UPLOAD_HISTORY_DETAILS,
  INITIATE_RULE_ENGINE,
  PROCESS_INTELLI_UPLOAD_DOCUMENTS,
} from "@/core/graphql/intelli-upload";
import { IntelliUploadQueueHistory } from "@/core/entities/IntelliUpload";

const _appConfig = AppConfig.getInstance();

const TIME_ZONE = _appConfig.TIMEZONE;

export class IntelliUploadRepository implements IIntelliUploadRepository {
  constructor(private readonly client: ApolloClient) {}

  async uploadNewDocument(request: IIntelliUploadNewDocumentRequest): Promise<IIntelliUploadNewDocumentResponse> {
    const formData = new FormData();
    formData.append("file", request.file);
    formData.append("queueId", request.queueId);
    return await new PrivateHttp({
      headers: {
        "Content-Type": ContentType.MULTIPART,
      },
    }).post(ApiEndpoints.INTELLI_UPLOAD_NEW_DOCUMENT, formData);
  }

  async clearDocuments(params: IIntelliUploadClearIntelliUploadDocumentsRequest): Promise<IIntelliUploadClearIntelliUploadDocumentsResponse> {
    const { data } = await this.client.mutate<IIntelliUploadClearIntelliUploadDocumentsResponse>({
      mutation: CLEAR_INTELLI_UPLOAD_DOCUMENTS,
      variables: { params },
    });

    if (!data) {
      throw new Error("Failed to clear intelli upload documents");
    }

    return {
      clearQueue: data.clearQueue,
    };
  }

  async processDocuments(params: IIntelliUploadProcessDocumentsRequest): Promise<IIntelliUploadProcessDocumentsResponse> {
    const { data } = await this.client.mutate<IIntelliUploadProcessDocumentsResponse>({
      mutation: PROCESS_INTELLI_UPLOAD_DOCUMENTS,
      variables: { params },
    });

    if (!data) {
      throw new Error("Failed to process intelli upload documents");
    }

    return {
      processQueue: {
        queueId: data.processQueue.queueId,
        documentCount: data.processQueue.documentCount,
        processId: data.processQueue.processId,
        message: data.processQueue.message,
        documents: data.processQueue.documents,
      },
    };
  }

  async initiateRuleEngine(params: IIntelliUploadInitiateRuleEngineRequest): Promise<IIntelliUploadInitiateRuleEngineResponse> {
    const { data } = await this.client.mutate<IIntelliUploadInitiateRuleEngineResponse>({
      mutation: INITIATE_RULE_ENGINE,
      variables: { params },
    });

    if (!data) {
      throw new Error("Failed to initiate rule engine");
    }
 
    await this.client.cache.reset();

    return {
      initiateRuleEngine: data.initiateRuleEngine,
    };
  }

  async getIntelliUploadHistory(params: IIntelliUploadHistoryRequest): Promise<IIntelliUploadHistoryResponse> {
    const { data } = await this.client.query<IIntelliUploadHistoryResponse>({
      query: GET_INTELLI_UPLOAD_HISTORY,
      variables: {
        params: {
          pageNumber: params.pageNumber,
          pageSize: params.pageSize,
          timeZone: params.timeZone ?? TIME_ZONE,
        },
      },
      fetchPolicy: "network-only",
    });

    if (!data) {
      throw new Error("Failed to get intelli upload history");
    }

    return {
      queueList: {
        total: data.queueList.total,
        page: data.queueList.page,
        limit: data.queueList.limit,
        items: data.queueList.items.map(item => new IntelliUploadQueueHistory(item)),
      },
    };
  }

  async getIntelliUploadHistoryDetails(params: Omit<IIntelliUploadProcessDocumentsRequest, "documents">): Promise<IIntelliUploadProcessDocumentsResponse> {
    const { data } = await this.client.query<IIntelliUploadProcessDocumentsResponse>({
      query: GET_INTELLI_UPLOAD_HISTORY_DETAILS,
      variables: { params },
      fetchPolicy: "network-only",
    });

    if (!data) {
      throw new Error("Failed to get intelli upload history details");
    }

    return {
      queueDetails: data.queueDetails,
    };
  }
}
